
import DashboardService from "../../services/dashboard-service";
import { ORDER_STATUS } from "@/helpers/order/status";
import indexDbService from "@/helpers/dbHelper";
import liveObjectsService from "@/plugins/liveObjects";
import {
  REMOTE_ORDER_SYNC_BACKOFF_MS
} from "@/store/appConstants";
import {
  buildOrderSyncOutboxEntry,
  getOrderSyncKey,
  getOrderSyncRevision,
  getOrderSyncStateSignature,
  isQueueableOrder,
  isTerminalOrderStatus,
  normalizeOrderForSync
} from "@/helpers/order/orderSyncOutbox";
import {
  autoSendOnlineOrderToKitchen,
  isOnlineKitchenAutoSendEnabled
} from "@/helpers/order/onlineOrderAutoKitchen";
import { normalizeOrderDiscounts } from "@/helpers/order/normalizeOrderDiscounts";
import { buildOrderFromKitchenHub } from "@/helpers/order/kitchenHubOrderMapper";

const removeOlderThenDays = 5;
let drainOrderSyncOutboxPromise = null;
let drainOrderSyncOutboxNeedsRerun = false;
let reconcileOrdersInFlight = null;
// Another terminal broadcasts a close over Ably before its own save has
// landed — and saves go through a queue service, so the backend can lag by
// well over a minute. A single open-orders fetch right at reconnect can still
// see the order as open; re-check a few times instead of trusting one read.
const GHOST_RECHECK_DELAYS_MS = [10000, 45000, 120000];
let ghostRecheckTimers = [];

const scheduleGhostRechecks = (dispatch) => {
  ghostRecheckTimers.forEach(clearTimeout);
  ghostRecheckTimers = GHOST_RECHECK_DELAYS_MS.map(delay => setTimeout(() => {
    // Ably first: it carries the other terminal's close long before the
    // queued backend write does.
    dispatch('applyOrdersSnapshot')
      .then(() => dispatch('fetchOpenOrders'))
      .catch(e => console.warn('[Orders] Ghost re-check failed:', e));
  }, delay));
};
const createRemoteOrderInFlight = new Map();
/** local_ids that already had a single create-blank attempt (success or failure). */
const createRemoteOrderAttempted = new Set();
let resolveOrderIdsBackoffUntil = 0;
// Tracks order IDs currently being auto-sent to the kitchen so concurrent
// paths (Ably + Pusher 5s timer) never double-print the same order.
const _autoSendInFlight = new Set();
// Tracks KitchenHub order ids currently being fetched/built so a Pusher retry
// (or the same event landing twice) never builds/syncs the same order twice.
const _kitchenHubReceiveInFlight = new Set();

const getOrderSyncComparableTime = (order) => {
  if (!order) return 0;
  return new Date(order.updated_at || order.close_date || order.hold_date || order.created_at || 0).getTime() || 0;
};

const selectOrderSyncCandidate = (queuedPayload, latestEntry) => {
  if (!latestEntry || !latestEntry.payload) {
    return {
      payload: queuedPayload,
      revision: getOrderSyncRevision(queuedPayload)
    };
  }

  const latestPayload = latestEntry.payload;
  const queuedIsTerminal = isTerminalOrderStatus(queuedPayload && queuedPayload.status);
  const latestIsTerminal = isTerminalOrderStatus(latestPayload.status);

  // A terminal queue intent (completed/cancelled/refunded) is never allowed to
  // be replaced by an older open/hold copy from Vuex. This keeps all screens on
  // the same outbox path without relying on trigger names.
  if (queuedIsTerminal && !latestIsTerminal) {
    return {
      payload: queuedPayload,
      revision: getOrderSyncRevision(queuedPayload)
    };
  }

  if (!queuedIsTerminal && latestIsTerminal) {
    return latestEntry;
  }

  if (latestPayload.id && queuedPayload && !queuedPayload.id) {
    return latestEntry;
  }

  const queuedTime = getOrderSyncComparableTime(queuedPayload);
  const latestTime = getOrderSyncComparableTime(latestPayload);
  return latestTime > queuedTime ? latestEntry : {
    payload: queuedPayload,
    revision: getOrderSyncRevision(queuedPayload)
  };
};

const cloneOrderPayload = (order) => {
  if (!order) return null;
  try {
    return JSON.parse(JSON.stringify(order));
  } catch (error) {
    console.warn("[Orders] Failed to clone order payload for broadcast", error);
    return { ...order };
  }
};

const broadcastOrderUpdate = (order) => {
  if (!order || !liveObjectsService.isReady()) return;
  // Last line of defense: never publish line-style item discounts into the
  // persistent Ably LiveMap, where they would corrupt every other terminal.
  const payload = cloneOrderPayload(normalizeOrderDiscounts(order));
  if (!payload) return;
  payload.source = liveObjectsService.getClientId();
  payload.updated_at = payload.updated_at || (new Date).toISOString();
  payload.local_id = payload.local_id || payload.id;
  // Ensure number field is set to id for Ably sync (required by backend)
  if (payload.id) {
    payload.number = payload.id.toString();
  } else if (!payload.number) {
    payload.number = "0";
  }
  liveObjectsService.publishOrderUpdate(payload);
};

// Peers that received this order while it only had a local_id keep that copy
// until this terminal next saves it; publish the server id as soon as it is
// known so they can match by local_id and pick it up straight away.
const broadcastAssignedOrderId = (state, localId) => {
  const order = state.orders.find(o => o.local_id === localId);
  if (order && order.id != null) {
    broadcastOrderUpdate(order);
  }
};

const broadcastOrderDeletion = (identifier = {}) => {
  if (!liveObjectsService.isReady()) return;
  const payload = {
    source: liveObjectsService.getClientId(),
    id: identifier.id || null,
    local_id: identifier.local_id || identifier.id || null
  };
  if (!payload.id && !payload.local_id) {
    return;
  }
  liveObjectsService.publishOrderDeletion(payload);
};

const extractRealtimeMeta = (order) => {
  if (!order || typeof order !== "object") {
    return { cleanOrder: order, fromRealtime: false, fromIndexedDb: false };
  }
  const { __fromRealtime, __fromIndexedDb, ...cleanOrder } = order;
  return { cleanOrder, fromRealtime: !!__fromRealtime, fromIndexedDb: !!__fromIndexedDb };
};

// ── Phase 3 Vuex hygiene: saveOrder side-effect handoff ─────────────────────
// The `saveOrder` mutation is pure (state only). Instead of writing to
// IndexedDB / broadcasting over Ably inline, it RECORDS the persistence and
// broadcast work it would have done into this module-scoped slot. The
// `saveOrder` ACTION reads the slot immediately after the (synchronous) commit
// and performs the I/O. Mutations run synchronously and nothing awaits between
// commit and flush, so the recorded object references stay valid. This keeps
// the exact persistence/broadcast behavior while removing side effects from the
// mutation. Callers MUST dispatch `orders/saveOrder` (never commit it directly)
// for the I/O to run.
let pendingSaveOrderSideEffects = null;

const flushSaveOrderSideEffects = () => {
  const effects = pendingSaveOrderSideEffects;
  pendingSaveOrderSideEffects = null;
  if (!effects) return;
  (effects.persist || []).forEach(({ store, order }) => {
    if (order) indexDbService.saveToDb(store, order);
  });
  if (effects.broadcast) {
    broadcastOrderUpdate(effects.broadcast);
  }
};

const state = () => ({
  orders: [],
  updating_order: {
    local_id: null,
    remote_id: null
  },
  refundItems: [],
  refundOrderId: null,
  deductRefundQuantity: true,
  refundedOrderTableId:null,
  orderList: [],
  refundedOrdersList: [],
  pagination: 1,
  noMoreData: false,
  noMoreRefundData: false,
  isLoad: false,
  emptyCurrentOrder: false,
  syncedOrderList: [],
  isActiveOrder:false,
  refundedOrderDiscountTotalDetails: {
    value: 0,
    dsicountType:0
  },
  tableOrders:[],
  orderInitialSync:false,
  wholeOrderDiscount:{
    value:0,

  },
  selectedOrder:null,
  updatedGiftCardPayments:[],
  requestedPath: null,
  isRealtimeReconciliationInProgress: false,
});

// getters
const getters = {
  isRealtimeReconciliationInProgress: state => state.isRealtimeReconciliationInProgress,
  getUpdatingOrder: (state) => state.updating_order,
  getOrderByIdOrLocalId: (state) => (id) => state.orders.find(order => (order.id && order.id === id) || (order.local_id && order.local_id === id)) || state.orderList.find(order => (order.id && order.id === id) || (order.local_id && order.local_id === id)),
  getOrderLocalIdOrById: (state) => (id) => state.orderList.find(order => (order.id && order.id === id) || (order.local_id && order.local_id === id)) || state.orders.find(order => (order.id && order.id === id) || (order.local_id && order.local_id === id)),
  getAllOrders: (state) => state.orders,
  getOrdersByStatus: (state) => (status) => state.orders.filter(o => o.status === status),
  getAllActiveOrders: (state) => state.orders.filter(o => o.status === ORDER_STATUS.OPEN || o.status === ORDER_STATUS.HOLD),
  refundItems: (state) => state.refundItems,
  refundOrderId: state => state.refundOrderId,
  shouldDeductQuantity: state => state.deductRefundQuantity,
  refundedOrderTableId: state => state.refundedOrderTableId,
  orderList: state => state.orderList,
  refundedOrdersList: state => state.refundedOrdersList,
  isLoad: state => state.isLoad,
  emptyCurrentOrder: state => state.emptyCurrentOrder,
  isActiveOrder: state => state.isActiveOrder,
  refundedOrderDiscountTotalDetails: state => state.refundedOrderDiscountTotalDetails,
  tableOrders:state=> state.tableOrders,
  orderInitailSync : state => state.orderInitialSync,
  wholeOrderDiscount: state => state.wholeOrderDiscount,
  updatingGiftCardPayments: state => state.updatedGiftCardPayments,
  showSelectedOrder: state => state.selectedOrder,
  requestedPath: state => state.requestedPath
};

// mutations
const mutations = {
  setRealtimeReconciliationStatus(state, status) {
    state.isRealtimeReconciliationInProgress = status;
  },
  removeOldOrders (state) {
    const dateInPast = new Date();
    dateInPast.setDate(dateInPast.getDate() - removeOlderThenDays);
    state.orders = state.orders.filter(o => {
      return !(((new Date(o.updated_at)) < dateInPast) && o.status === ORDER_STATUS.COMPLETED);
    });
  },
  updateOrders(state, payload) {
    const now = (new Date).toISOString();
    if (!Array.isArray(payload))
      payload = [{ ...payload }]

    for (const retrievedOrder of payload) {
      if (!retrievedOrder) continue;
      const { cleanOrder, fromRealtime, fromIndexedDb } = extractRealtimeMeta(retrievedOrder);
      const safeNumber = (value) => {
        const num = typeof value === "number" ? value : parseFloat(value);
        return Number.isFinite(num) ? num : 0;
      };
      // Realtime payloads without updated_at are treated as stale - do not default to now
      // so we can skip overwriting local state (see condition below).
      // Normalize item-level discounts from server convention (total line
      // discount) to POS convention (per-unit). The helper reconciles the
      // items against the order's own discount_total to decide which
      // convention the payload uses, so it is idempotent for both.
      const discountNormalizedOrder = normalizeOrderDiscounts(cleanOrder);
      const normalizedOrder = {
        ...discountNormalizedOrder,
        local_id: retrievedOrder.local_id || retrievedOrder.id,
        synced_at: now,
        updated: fromIndexedDb ? cleanOrder.updated : false,
        updated_at: cleanOrder.updated_at || (fromRealtime ? undefined : now),
        subtotal: safeNumber(cleanOrder.subtotal),
        tax_total: safeNumber(cleanOrder.tax_total),
        grand_total: (() => {
          // biyo-order sends grand_total = subtotal + tax + tips; Django saves it as
          // subtotal + tax (no tips). Normalize to always exclude tips so display
          // formulas can safely add tips_total on top without double-counting.
          const gt = safeNumber(cleanOrder.grand_total);
          const tips = safeNumber(cleanOrder.tips_total);
          const base = safeNumber(cleanOrder.subtotal) + safeNumber(cleanOrder.tax_total) - safeNumber(cleanOrder.discount_total);
          return (tips > 0 && gt > base + tips * 0.5) ? parseFloat((gt - tips).toFixed(4)) : gt;
        })(),
        total_sum: safeNumber(cleanOrder.grand_total)
      };
      // Ensure number field is set to id (required by backend and for consistency)
      if (normalizedOrder.id) {
        normalizedOrder.number = normalizedOrder.id.toString();
      } else if (!normalizedOrder.number) {
        // If no id yet, keep existing number or set to "0"
        normalizedOrder.number = normalizedOrder.number || "0";
      }
      const orderIndex = state.orders.findIndex(order => {
        if (normalizedOrder.id && order.id === normalizedOrder.id) return true;
        if (normalizedOrder.local_id && order.local_id === normalizedOrder.local_id) return true;
        return false;
      });
      if (orderIndex === -1) {
        state.orders.push(normalizedOrder);
        indexDbService.saveToDb('orders', state.orders[state.orders.length - 1]);

      } else {
        const retrievedUpdateDate = normalizedOrder.updated_at ? new Date(normalizedOrder.updated_at).getTime() : null;
        const localUpdateDate = state.orders[orderIndex].updated_at ? new Date(state.orders[orderIndex].updated_at).getTime() : null;
        const localOrder = state.orders[orderIndex];

        // A payload can lose every arbitration check below (protected local
        // edits, clock skew between this terminal and a customer's phone,
        // missing timestamp) and still carry ITEMS this terminal has never
        // seen — e.g. a table-side ordering round added while staff had the
        // order open. Items are append-only facts: rejecting the rest of the
        // payload must never make those items invisible, so the rejection
        // paths merge unknown items in (mirroring the race-condition merge
        // below, and with the same trade-off: totals are not recalculated
        // here, the next local edit or server sync trues them up).
        const mergeRemoteOnlyItemsIntoLocal = () => {
          const current = state.orders[orderIndex];
          const remoteItems = Array.isArray(normalizedOrder.items) ? normalizedOrder.items : [];
          const localItems = Array.isArray(current.items) ? current.items : [];
          if (!remoteItems.length) return;
          const localIds = new Set(localItems.map(i => i.id).filter(Boolean));
          const localLocalIds = new Set(localItems.map(i => i.local_id).filter(Boolean));
          const unseenItems = remoteItems.filter(remoteItem =>
            !(remoteItem.id && localIds.has(remoteItem.id)) &&
            !(remoteItem.local_id && localLocalIds.has(remoteItem.local_id))
          );
          if (!unseenItems.length) return;
          state.orders[orderIndex] = { ...current, items: [...localItems, ...unseenItems] };
          state.orders = [...state.orders];
          indexDbService.saveToDb('orders', state.orders[orderIndex]);
        };

        // Ably/LiveObjects: do not overwrite local order with realtime payload that has no timestamp (stale)
        // Exception: always apply when remote status is closed (COMPLETED/CANCELLED/REFUNDED) so that
        // when another terminal pays/closes the order, this terminal stops showing it on tables (no "ghost").
        const isClosedStatus = (s) => s === ORDER_STATUS.COMPLETED || s === ORDER_STATUS.CANCELLED || s === ORDER_STATUS.REFUNDED;
        const localIsTerminal = isClosedStatus(localOrder.status);
        const remoteIsTerminal = isClosedStatus(normalizedOrder.status);
        const localSyncedDate = localOrder.synced_at ? new Date(localOrder.synced_at).getTime() : 0;
        const localHasUnsyncedChanges = localOrder.updated === true && (!localUpdateDate || localUpdateDate > localSyncedDate);
        const localIsProtected = localOrder.updated === true;
        const remoteMatchesProtectedLocalState =
          getOrderSyncStateSignature(normalizedOrder) === getOrderSyncStateSignature(localOrder);

        // A closed status from another terminal beats pending local edits: the
        // order is finished elsewhere, and keeping it open here only leaves a
        // ghost that no later sync will clear (the protected branch below
        // never applies status). Reverse direction (local closed, remote open)
        // is still protected by the checks that follow.
        if (remoteIsTerminal && !localIsTerminal) {
          state.orders[orderIndex] = { ...localOrder, ...normalizedOrder };
          state.orders[orderIndex].local_id = state.orders[orderIndex].local_id || state.orders[orderIndex].id;
          state.orders = [...state.orders];
          indexDbService.saveToDb('orders', state.orders[orderIndex]);
          continue;
        }

        // Local unsynced edits are the highest-priority source of truth. API and
        // Ably may lag behind the queue/outbox, so they must not roll a local
        // cancel/close/payment back to OPEN while updated:true is pending.
        if (localIsProtected && (!fromRealtime || localHasUnsyncedChanges)) {
          if (normalizedOrder.id && !localOrder.id) {
            state.orders[orderIndex] = {
              ...localOrder,
              id: normalizedOrder.id,
              number: normalizedOrder.number || normalizedOrder.id.toString()
            };
            state.orders = [...state.orders];
            indexDbService.saveToDb('orders', state.orders[orderIndex]);
          }
          if (!fromRealtime && remoteMatchesProtectedLocalState && retrievedUpdateDate >= localUpdateDate) {
            state.orders[orderIndex] = { ...localOrder, ...normalizedOrder, updated: false };
            state.orders = [...state.orders];
            indexDbService.saveToDb('orders', state.orders[orderIndex]);
          } else if (!isClosedStatus(state.orders[orderIndex].status)) {
            mergeRemoteOnlyItemsIntoLocal();
          }
          continue;
        }

        // A terminal local status should never be downgraded by a non-terminal
        // remote/API payload. The server can be stale while the queue is still
        // processing the local terminal update.
        if (localIsTerminal && !remoteIsTerminal) {
          continue;
        }

        if (fromRealtime && localUpdateDate && !retrievedUpdateDate && !isClosedStatus(normalizedOrder.status)) {
          mergeRemoteOnlyItemsIntoLocal();
          continue;
        }

        // ==========================================
        // RACE CONDITION FIX (Sync Overwrite Protection)
        // ==========================================
        // Before overwriting the order with server data, we must check if we have
        // local items that are missing from the server response (e.g. added during slow 3G sync).
        if (normalizedOrder.items && Array.isArray(normalizedOrder.items)) {
          const currentLocalItems = state.orders[orderIndex].items || [];

          // Items this terminal already holds arrived POS-shaped (via Ably or
          // local edits): kitchen-sent flags, printers, void_status conventions.
          // The backend echoes a different shape and does not carry POS-only
          // fields, so an API payload only fills in what the local item lacks
          // (its server id, mostly) and never replaces it. Realtime payloads
          // come from the terminal that made the change and apply as-is.
          if (!fromRealtime && !fromIndexedDb && currentLocalItems.length > 0) {
            const localByKey = new Map();
            currentLocalItems.forEach(i => {
              if (i.id != null) localByKey.set(`id:${i.id}`, i);
              if (i.local_id) localByKey.set(`local:${i.local_id}`, i);
            });
            normalizedOrder.items = normalizedOrder.items.map(item => {
              const local = (item.id != null && localByKey.get(`id:${item.id}`)) ||
                (item.local_id && localByKey.get(`local:${item.local_id}`));
              if (!local) return item;
              return { ...item, ...local, id: local.id != null ? local.id : item.id };
            });
          }

          const serverItems = normalizedOrder.items;

          if (currentLocalItems.length > 0) {
            const serverIds = new Set(serverItems.map(i => i.id));
            const serverLocalIds = new Set(serverItems.map(i => i.local_id));

            // Find items present locally but NOT in server response
            const unsavedItems = currentLocalItems.filter(localItem => {
              const inServer = (localItem.id && serverIds.has(localItem.id)) ||
                  (localItem.local_id && serverLocalIds.has(localItem.local_id));
              // Keep the item if it's not in the server response
              return !inServer;
            });

            // If we found such items, merge them back into the payload
            if (unsavedItems.length > 0) {
              normalizedOrder.items = [...serverItems, ...unsavedItems];
              // Note: We deliberately don't recalculate totals here.
              // The next local interaction or sync will fix totals.
              // Priority is preventing data loss of items.
            }
          }
        }

        if ((retrievedUpdateDate >= localUpdateDate) || !localUpdateDate) {
          state.orders[orderIndex] = { ...state.orders[orderIndex], ...normalizedOrder };
          state.orders = [...state.orders]
        } else if (retrievedUpdateDate < localUpdateDate) {
          // Do not let stale API/Ably payloads roll back newer local edits such
          // as moving an order between tables while the queue/server is lagging.
          // The only older remote payload we still accept is a terminal status
          // from another terminal, and only when this terminal has no pending
          // local mutation for the same order.
          if (remoteIsTerminal && !localHasUnsyncedChanges && !localIsTerminal) {
            state.orders[orderIndex] = { ...state.orders[orderIndex], ...normalizedOrder };
            state.orders = [...state.orders]
          } else {
            // "Older" here can just be clock skew between machines (a customer
            // phone vs this terminal) — never let that hide items we've not seen.
            mergeRemoteOnlyItemsIntoLocal();
          }
        }
        state.orders[orderIndex].local_id = state.orders[orderIndex].local_id || state.orders[orderIndex].id;
        indexDbService.saveToDb('orders', state.orders[orderIndex]);
      }
    }
  },
  saveOrder (state, payload) {
    // Pure mutation: never touches IndexedDB / Ably. Records the I/O it would
    // have done into pendingSaveOrderSideEffects; the saveOrder action flushes.
    pendingSaveOrderSideEffects = null;
    if (!payload) return;

    // Skip blank placeholder orders that have never had any content.
    // isActive:false + no items + no payments + no server id = it's the empty
    // initialisation object; saving it would mark it updated:true and cause it
    // to be included in every upload batch forever (because id:0 is falsy).
    if (!payload.id &&
        payload.isActive === false &&
        (!payload.items || payload.items.length === 0) &&
        (!payload.payments || payload.payments.length === 0)) {
      return;
    }

    const now = (new Date).toISOString();
    const { cleanOrder: rawCleanOrder, fromRealtime } = extractRealtimeMeta(payload);
    // Raw server payloads can reach saveOrder directly (auto-kitchen save,
    // background fetchOrderById) carrying line-style item discounts. Convert
    // to the POS per-unit convention before this state is persisted locally
    // and broadcast to other terminals. No-op for POS-calculated orders.
    const cleanOrder = normalizeOrderDiscounts(rawCleanOrder);
    const incomingOriginalUpdatedAt = cleanOrder && cleanOrder.updated_at
      ? new Date(cleanOrder.updated_at).getTime()
      : null;

    payload = {
      ...cleanOrder, ...{
        created_at: payload.id ? payload.created_at : now,
        updated_at: now,
        updated: true
      }
    };
    // Ensure open_date is always populated before syncing to backend
    if (!payload.open_date) {
      payload.open_date = cleanOrder.open_date || payload.created_at || now;
    }
    // Ensure number field is set to id (required by backend API)
    // If order has an id, number should match it
    if (payload.id) {
      payload.number = payload.id.toString();
    }

    const existingOrderIndex = state.orders.findIndex(order => (payload.id && order.id === payload.id) || (payload.local_id && (order.local_id === payload.local_id)));
    const foundRefundOrder = state.refundedOrdersList.findIndex(refundedOrder=>refundedOrder.local_id === payload.local_id)
    const effects = { persist: [], broadcast: null };
    if (existingOrderIndex === -1) {
      state.orders.push(payload);
      // // updating refund order list as well
      if (payload.grand_total < 0.00) {
        if (foundRefundOrder === -1) {
          state.refundedOrdersList.push(payload)
        }
          effects.persist.push({ store: 'refundedOrderList', order: payload });
      }
      effects.persist.push({ store: 'orders', order: payload });
      if (!fromRealtime) {
        effects.broadcast = payload;
      }
    } else {
      const existingOrder = state.orders[existingOrderIndex];
      const existingUpdatedAt = existingOrder && existingOrder.updated_at
        ? new Date(existingOrder.updated_at).getTime()
        : null;
      const terminalStatuses = [ORDER_STATUS.COMPLETED, ORDER_STATUS.CANCELLED, ORDER_STATUS.REFUNDED];
      const isIncomingTerminal = terminalStatuses.includes(payload.status);

      // A component can hold an old copy while another local action already
      // saved a newer order state (for example: move table, then the old Order
      // route destroys and tries to save its stale currentOrder). Do not stamp
      // that stale copy with "now" and overwrite the newer business state.
      if (!fromRealtime &&
          incomingOriginalUpdatedAt &&
          existingUpdatedAt &&
          incomingOriginalUpdatedAt < existingUpdatedAt &&
          !isIncomingTerminal) {
        if (payload.locked_by !== undefined && existingOrder.locked_by !== payload.locked_by) {
          state.orders[existingOrderIndex] = {
            ...existingOrder,
            locked_by: payload.locked_by
          };
          state.orders = [...state.orders];
          effects.persist.push({ store: 'orders', order: state.orders[existingOrderIndex] });
        }
        pendingSaveOrderSideEffects = effects;
        return;
      }

      const oldStatus = state.orders[existingOrderIndex].status;
      if ((oldStatus === ORDER_STATUS.COMPLETED || oldStatus === ORDER_STATUS.CANCELLED || oldStatus === ORDER_STATUS.REFUNDED) && oldStatus !== payload.status) {
        console.error(`You can not update status of orders with status Completed or Canceled, oldStatus: ${oldStatus}, newStatus: ${payload.status}, order: ${state.orders[existingOrderIndex].id || state.orders[existingOrderIndex].local_id}, not saved...`);
        return;
      }
      // Preserve payments if backend doesn't return them (this is critical for Dejavoo integration)
      const existingPayments = state.orders[existingOrderIndex].payments;
      const hasExistingPayments = existingPayments && existingPayments.length > 0;
      const backendHasPayments = payload.payments && payload.payments.length > 0;

      if (hasExistingPayments && !backendHasPayments) {
        payload.payments = existingPayments;
      }

      state.orders = [...state.orders.map((item, itemIndex) => itemIndex !== existingOrderIndex ? item : { ...item, ...payload })];
      state.syncedOrderList.push(payload)
      if (payload.grand_total < 0.00) {
        state.refundedOrdersList[foundRefundOrder] = { ...state.refundedOrdersList[foundRefundOrder], ...payload }
        state.refundedOrderList = [...state.refundedOrdersList]
      }
      state.orders = [...state.orders]
      effects.persist.push({ store: 'orders', order: state.orders[existingOrderIndex] });
      if (!fromRealtime) {
        effects.broadcast = state.orders[existingOrderIndex];
      }
    }
    pendingSaveOrderSideEffects = effects;
  },
  updateOrderId (state, payload) {
    // Some endpoints (or bulk uploads) may return orders without local_id.
    // In that case, fall back to matching by id and use the existing local_id
    // from state so UI watchers (like Order.vue) can react properly.
    let localId = payload.local_id;
    let objIndex = -1;
    if (localId) {
      objIndex = state.orders.findIndex(obj => obj.local_id === localId);
    } else if (payload.id) {
      objIndex = state.orders.findIndex(obj => obj.id === payload.id);
      if (objIndex > -1) {
        localId = state.orders[objIndex].local_id;
      }
    }

    if (!localId || objIndex === -1) {
      return;
    }

    if (objIndex > -1) {
      if (payload.id != null) {
        state.orders[objIndex].id = payload.id;
        if (payload.number != null) state.orders[objIndex].number = payload.number;
        state.updating_order = {
          local_id: localId,
          remote_id: payload.id
        };
      }
      if (payload.synced_at != null) {
        state.orders[objIndex].synced_at = payload.synced_at;
      }
      if (payload.updated !== undefined) {
        state.orders[objIndex].updated = payload.updated;
      }
      indexDbService.saveToDb('orders', state.orders[objIndex]);
    }
  },

  // async updateGiftPaymentId(state, {updatedOrder, index}){
  //     if(updatedOrder.order_gift_card_payment.length > 0){
  //       state.orders[index].order_gift_card_payment = updatedOrder.order_gift_card_payment
  //       state.updatedGiftCardPayments = updatedOrder.order_gift_card_payment
  //     }

  // },

  // Mark an order as successfully synced WITHOUT re-stamping updated_at or updated:true.
  // Use this instead of saveOrder after a successful queue POST to avoid the
  // feedback loop where saveOrder's unconditional updated:true triggers another sync.
  markOrderSynced(state, { id, local_id, synced_at, last_synced_revision }) {
    const idx = state.orders.findIndex(o =>
      (id && o.id === id) || (local_id && o.local_id === local_id)
    );
    if (idx === -1) return;
    state.orders[idx].synced_at = synced_at;
    if (last_synced_revision !== undefined) {
      state.orders[idx].last_synced_revision = last_synced_revision;
    }
    state.orders = [...state.orders];
    indexDbService.saveToDb('orders', state.orders[idx]);
  },

  updateLocalIds (state, context) {
    const ordersWithLocalIds = state.orders.filter(o => {
      return (o.customer && o.customer.toString().includes("_")) || (o.shift && o.shift.toString().includes("_"));
    });
    ordersWithLocalIds.forEach((o, oIndex) => {
      if (o.customer.toString().includes("_")) {
        const customer = context.rootState.getters["customers/getAllCustomers"].find(c => c.local_id === o.customer);
        if (customer && customer.id) {
          state.orders[oIndex].customer = customer.id;
        } else {
          console.warn("Could not find customer to replace local_id to id");
        }
      }
      if (o.shift.toString().includes("_")) {
        const shift = context.rootState.getters["shifts/getAllShifts"].find(s => s.local_id === o.shift);
        if (shift && shift.id) {
          state.orders[oIndex].shift = shift.id;
        }
      }
    });
  },
  setRefundItems(state, refundItems) {
    state.refundItems = refundItems;
  },
  setRefundOrderId(state, refundId) {
    state.refundOrderId = refundId;
  },
  setDeductRefundQuantity(state, isMore) {
    state.deductRefundQuantity = isMore
  },
  setRefundOrderTableId(state, tableId) {
    state.refundedOrderTableId= tableId
   },
  setRequestedPath(state, payload) {
    state.requestedPath = payload
   },
  fetchOrderListByStatus(state, orderList) {
    // Server payloads use line-style item discounts; convert to the POS
    // per-unit convention before they enter state/IndexedDB.
    orderList = Array.isArray(orderList) ? orderList.map(normalizeOrderDiscounts) : orderList;
    const removeDuplicateOrders = orderList.filter(order => {
      if (state.orderList.findIndex(exsistingOrder => (exsistingOrder.id === order.id)) === -1)
        return order
      else {
        let index = state.orderList.findIndex(exsistingOrder => (exsistingOrder.id === order.id))
        state.orderList[index] = order
      }
    })
    state.orderList.push(...removeDuplicateOrders)
    indexDbService.saveToDb('orders',removeDuplicateOrders)
  },
  fetchRefundedOrderList(state, refundedOrderList) {
    const removeDuplicateOrders = refundedOrderList.filter(order => {
      if (state.refundedOrdersList.findIndex(exsistingOrder => (exsistingOrder.id === order.id)) === -1)
        return order
      else {
        let index = state.refundedOrdersList.findIndex(exsistingOrder => (exsistingOrder.id === order.id))
        state.refundedOrdersList[index] = order
        indexDbService.saveToDb('refundedOrderList',state.refundedOrdersList[index])
      }
    })
    state.refundedOrdersList.push(...removeDuplicateOrders)
    indexDbService.saveToDb('refundedOrderList',removeDuplicateOrders)
  },
  emptyOrderList(state) {
    state.orderList = []
    state.refundedOrdersList = []
    state.orders = []
  },
  updateOrderList(state, order) {
    state.orderList.push(...order)
  },
  setEmptyCurrentOrder(state, isEmptyCurrentOrder) {
    state.emptyCurrentOrder = isEmptyCurrentOrder
  },
  setSyncedOrder: (state, syncedOrder) => state.syncedOrderList.push(syncedOrder),
  setActiveOrderState: (state, isActiveOrder) => state.isActiveOrder = isActiveOrder,
  setRefundedOrderTotalDiscount: (state, refundedOrderDiscountTotalDetails) => state.refundedOrderDiscountTotalDetails = refundedOrderDiscountTotalDetails,
  setTableOrders:(state,tableOrders)=>state.tableOrders  = tableOrders,
  setDbOrders: (state, orders) => state.orderList = orders,
  setOrderInitailSync : (state,initialSync) => state.orderInitialSync = initialSync,
  setWholeOrderDiscount : (state,discount) => state.wholeOrderDiscount.value = discount,
  setSelectedOrder: (state, orderId) => {state.selectedOrder = orderId},
  // Pagination / loading flags previously mutated directly inside the
  // fetchOrderListByStatus action (Phase 3 Vuex hygiene: actions commit, not mutate).
  setOrderListPagination: (state, value) => state.pagination = value,
  setOrderListLoad: (state, value) => state.isLoad = value,
  setNoMoreData: (state, value) => state.noMoreData = value,
  setNoMoreRefundData: (state, value) => state.noMoreRefundData = value,
  // Stamp synced_at on an existing order (used by resolveOrderIdsByLocalIds so
  // the action no longer mutates state.orders directly). Mirrors the existing
  // pattern of persisting the touched order to IndexedDB.
  setOrderSyncedAt (state, { local_id, id, synced_at }) {
    const idx = state.orders.findIndex(o =>
      (local_id && o.local_id === local_id) || (id && o.id === id)
    );
    if (idx === -1) return;
    state.orders[idx].synced_at = synced_at;
    indexDbService.saveToDb('orders', state.orders[idx]);
  },
  // Flag an order as having unsynced local changes (used by enqueueOrderSync so
  // the action no longer mutates state.orders directly).
  markOrderUpdatedFlag (state, { order_id, order_local_id }) {
    const idx = state.orders.findIndex(item =>
      (order_id && item.id === order_id) ||
      (order_local_id && item.local_id === order_local_id)
    );
    if (idx === -1) return;
    state.orders[idx].updated = true;
    state.orders = [...state.orders];
  },
  removeOrderByIdentifier (state, payload = {}) {
    const matches = (order) => {
      if (!order) return false;
      if (payload.id && order.id === payload.id) return true;
      if (payload.local_id && order.local_id === payload.local_id) return true;
      return false;
    };

    const removeFromArray = (collection) => {
      const index = collection.findIndex(matches);
      if (index > -1) {
        const [removed] = collection.splice(index, 1);
        return removed;
      }
      return null;
    };

    const removedOrder = removeFromArray(state.orders);
    state.orderList = state.orderList.filter(order => !matches(order));
    state.refundedOrdersList = state.refundedOrdersList.filter(order => !matches(order));

    if (removedOrder) {
      const key = removedOrder.local_id || removedOrder.id;
      if (key) {
        indexDbService.deleteFromDb('orders', key);
        if (removedOrder.status === ORDER_STATUS.REFUNDED) {
          indexDbService.deleteFromDb('refundedOrderList', key);
        }
      }
    }
  },

  /**
   * Remove from state.orders any order that has a server id but is not in the server's open orders list.
   * Used after fetchOpenOrders so that when we were offline and another terminal closed an order,
   * we don't keep it as a "ghost" (still showing as open on tables).
   * Only removes by server id — orders with only local_id (e.g. just created offline, not yet in API response)
   * are kept so they don't disappear on the terminal that created them.
   */
  removeOrdersNotInOpenSet (state, payload) {
    const raw = Array.isArray(payload) || payload instanceof Set ? payload : payload?.openOrderIds;
    if (!Array.isArray(raw) && !(raw instanceof Set)) return;
    const idSet = raw instanceof Set ? raw : new Set((raw || []).map(id => id != null ? String(id) : null).filter(Boolean));
    const toRemove = state.orders.filter(o => o.id != null && !idSet.has(String(o.id)));
    if (toRemove.length > 0) {
      console.info('[Orders] Removing orders no longer open on server:', toRemove.map(o => o.id));
    }
    const localOnly = state.orders.filter(o => o.id == null);
    if (localOnly.length > 0) {
      console.info('[Orders] Keeping local-only orders (no server id):', localOnly.map(o => o.local_id));
    }
    toRemove.forEach(removed => {
      const key = removed.local_id || removed.id;
      if (key) indexDbService.deleteFromDb('orders', key);
    });
    state.orders = state.orders.filter(o => o.id == null || idSet.has(String(o.id)));
  }
};

const runOrderReconciliation = async ({ state, commit, dispatch }) => {
  commit('setRealtimeReconciliationStatus', true);

  try {
    await dispatch('applyOrdersSnapshot');

    // After reconciling with Ably snapshot, sync with server's open orders and remove ghosts
    // (e.g. order was paid on another terminal while we were offline — it won't be in snapshot
    // if backend removes it from the map, so we need fetchOpenOrders + removeOrdersNotInOpenSet).
    await dispatch('fetchOpenOrders');

    // Edits made while offline never reached Ably (publishing needs a live
    // connection) and the backend does not echo POS-only fields such as the
    // kitchen-sent flags, so hand them to peers directly. Anything a peer
    // closed meanwhile was already applied above and is no longer `updated`.
    const offlineEdited = state.orders.filter(o => o.updated === true);
    offlineEdited.forEach(o => broadcastOrderUpdate(o));
    if (offlineEdited.length > 0) {
      console.info(`[Orders] Republished ${offlineEdited.length} offline-edited orders to Ably`);
    }
  } catch (error) {
    console.error("[Orders] Realtime reconciliation failed:", error);
  } finally {
    commit('setRealtimeReconciliationStatus', false);
  }
};

// actions
const actions = {
  async reconcileOrdersWithRealtime(context) {
    if (!navigator.onLine) return;
    // Browser "online" and Ably "connected" can both fire for one reconnect;
    // run a single pass and let the outbox drain wait on it.
    if (reconcileOrdersInFlight) return reconcileOrdersInFlight;

    reconcileOrdersInFlight = runOrderReconciliation(context).finally(() => {
      reconcileOrdersInFlight = null;
      scheduleGhostRechecks(context.dispatch);
    });
    return reconcileOrdersInFlight;
  },
  // Re-applies the Ably orders map to the orders this terminal already holds.
  // Quiet (no overlay): runs on every map re-sync after a reconnect and on the
  // delayed re-checks, not just the initial reconciliation pass.
  async applyOrdersSnapshot({ state, commit }) {
    const snapshotOrders = await liveObjectsService.getAllOrdersSnapshot();
    if (!snapshotOrders || snapshotOrders.length === 0) return;

    let applied = 0;
    for (const remoteOrder of snapshotOrders) {
      const localOrder = state.orders.find(o =>
          (remoteOrder.id && o.id === remoteOrder.id) ||
          (remoteOrder.local_id && o.local_id === remoteOrder.local_id)
      );

      // Only update orders we already have locally. Do NOT add orders from the snapshot —
      // the Ably LiveMap contains all orders ever synced (hundreds/thousands). Adding them
      // would make the second terminal show many ghost orders on the tables view.
      if (!localOrder) continue;

      const remoteUpdated = remoteOrder.updated_at ? new Date(remoteOrder.updated_at).getTime() : 0;
      const localUpdated = localOrder.updated_at ? new Date(localOrder.updated_at).getTime() : 0;
      // A closed order is applied even when the local copy looks newer:
      // terminal clocks drift, and an offline edit made after another
      // terminal closed the order must not keep it alive here.
      if (remoteUpdated >= localUpdated || isTerminalOrderStatus(remoteOrder.status)) {
        commit('updateOrders', { ...remoteOrder, __fromRealtime: true });
        applied += 1;
      }
    }
    console.info(`[Orders] Ably snapshot applied to ${applied} of ${state.orders.length} local orders`);
  },
  saveOrder: ({ commit }, payload) => {
    // Pure mutation runs synchronously, then we perform the recorded
    // IndexedDB / Ably side effects (Phase 3 Vuex hygiene).
    commit('saveOrder', payload);
    flushSaveOrderSideEffects();
  },
  fetchOrders: async ({ commit, getters }) => {
    commit("removeOldOrders");
    const activeOrdersIds = getters.getAllActiveOrders.map(o => o.id || o.local_id);
    let retrieved = (await DashboardService.getOrders(activeOrdersIds)).results;
    await commit("updateOrders", retrieved);
  },
  uploadOrder: async ({ dispatch }, order) => {
    await dispatch("enqueueOrderSync", { order, trigger: "upload_order" });
    await dispatch("drainOrderSyncOutbox");
  },
  uploadOrders: async ({ dispatch }) => {
    await dispatch("uploadOfflineOrders");
  },
  initNewOrder ({ dispatch, rootGetters }, payload = {}) {
    const employee = rootGetters.employee;
    const terminal = rootGetters["config/terminal"];
    let newOrder = {
      local_id: `${terminal?.id || '000'}_${Date.now()}`,
      open_date: new Date().toISOString(),
      emp_open: employee ? employee.id : null,
      locked_by: terminal ? terminal.id : null,
      subtotal: 0,
      updated_at: new Date().toISOString(),
      items: [],
      tax_total: 0,
      status: 1,
      discount_total: 0,
      order_discount: 0,
      discount_orders: 0,
      grand_total: 0,
      number: "0",
      payments: []
    };
    newOrder = { ...newOrder, ...payload };
    // dispatch (not commit) so the recorded IndexedDB / Ably side effects run.
    dispatch("saveOrder", newOrder);
    dispatch("createRemoteOrder", newOrder.local_id);
    return newOrder;
  },
  async createRemoteOrder({ state, commit, rootGetters }, orderLocalId) {
    if (!window.navigator.onLine) return;
    if (!orderLocalId) return;

    const order = state.orders.find(o => o.local_id === orderLocalId);
    if (!order) return;

    if (order.id) {
      return;
    }

    const inFlightRequest = createRemoteOrderInFlight.get(orderLocalId);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    if (createRemoteOrderAttempted.has(orderLocalId)) {
      return;
    }

    const request = (async () => {
      createRemoteOrderAttempted.add(orderLocalId);
      try {
        const employee = rootGetters.employee;
        const payload = {
          ...order,
          emp_open: order.emp_open != null ? order.emp_open : (employee ? employee.id : null),
        };
        const response = await DashboardService.createBlankOrder(payload);

        if (response && response.id) {
            commit("updateOrderId", {
              local_id: order.local_id,
              id: response.id,
              number: response?.number || "0"
            });
            broadcastAssignedOrderId(state, order.local_id);
        }
      } catch (e) {
        console.warn('[Orders] Failed to create remote order:', e);
      } finally {
        createRemoteOrderInFlight.delete(orderLocalId);
      }
    })();

    createRemoteOrderInFlight.set(orderLocalId, request);

    try {
      return await request;
    } finally {
      // noop
    }
  },
  setRefundItems({ commit }, refundItems) {
    commit("setRefundItems", refundItems);
  },
  setRefundOrderId({ commit }, refundItem) {
    commit("setRefundOrderId",refundItem);
  },
  setDeductRefundQuantity({ commit }, isMore) {
    commit('setDeductRefundQuantity',isMore)
  },
  setRefundOrderTableId({ commit }, tableId) {
    commit('setRefundOrderTableId', tableId)
  },
  setRequestedPath({ commit }, payload) {
    commit('setRequestedPath', payload)
  },
  async fetchOrderListByStatus({ commit, state }, orderListDetails) {
    // only fetch data from the server if the application is online
    if (window.navigator.onLine) {
      try {
        let orders = []
        if (state.pagination < 1) {
          commit('setOrderListPagination', 1)
        }
        if (orderListDetails.initial) {
          commit('setOrderListPagination', 1)
          // state.isLoad = true
          let refundedOrders;
          commit('setNoMoreData', false)
          commit('setNoMoreRefundData', false)
          let data = (await DashboardService.getOrderList(orderListDetails.orderStatus, 1))
          orders = data.results
          if (orderListDetails.orderStatus === 3) {
            // added total sum variable
            orders = orders.map(order => {
              order.total_sum = parseFloat((order.grand_total).toFixed(2))
              return order
            })
            refundedOrders = (await DashboardService.getOrderList(5, 1))
            commit('fetchRefundedOrderList', refundedOrders.results)
            indexDbService.saveToDb("refundedOrderList",refundedOrders.results)
          }
          if (data.next) {
            commit('setOrderListPagination', state.pagination + 1)
          } else {
            commit('setNoMoreData', true)
          }

          if (refundedOrders && !refundedOrders.next) {
            commit('setNoMoreRefundData', true)
          }

          if (orderListDetails.onlineOrders) {
            orders = orders.filter(order => order.type === 1)
            await commit("updateOrders", orders);
          }
          else {
            commit('fetchOrderListByStatus', orders)
            indexDbService.saveToDb("orderList",orders)
            // Keep state.orders in sync for realtime-active views that rely on local orders.
            // updateOrders already protects newer local data from older server payloads.
            await commit("updateOrders", orders);
          }
          commit('setOrderListLoad', false)

        }
        else if (!state.noMoreData) {
          commit('setOrderListLoad', true)
          let data = (await DashboardService.getOrderList(orderListDetails.orderStatus, state.pagination))
          orders = data.results;
          let refundedOrders;
          if (orderListDetails.orderStatus === 3) {
            try {
              if (!state.noMoreRefundData) {
                refundedOrders = (await DashboardService.getOrderList(5, state.pagination)).results
                commit('fetchRefundedOrderList', refundedOrders)
              }

            }
            catch (err) {
              console.error(err);
             }
          }
          if (data.next) {
            commit('setOrderListPagination', state.pagination + 1)
          } else {
            commit('setNoMoreData', true)
          }

          if (refundedOrders && !refundedOrders.next) {
            commit('setNoMoreRefundData', true)
          }
          commit('fetchOrderListByStatus', orders)
          await commit("updateOrders", orders);
          indexDbService.saveToDb("orderList", orders)
          indexDbService.saveToDb("refundedOrderList",orders)
          commit('setOrderListLoad', false)
        }
      }
      catch (e) {
        commit('setOrderListLoad', false)
      }
    }
  },
  emptyOrderList({ commit }) {
    commit('emptyOrderList')
  },
  async fetchOrderById(_,orderId) {
    // Validate orderId before making API call
    if (!orderId || orderId === 'undefined') {
      console.error('[fetchOrderById] Invalid orderId:', orderId);
      throw new Error('Cannot fetch order: Invalid orderId');
    }
    const response = await DashboardService.getOrder(orderId);

    // API returns { results: [order] }, so get the first order from results array
    const orders = response.results;
    if (Array.isArray(orders) && orders.length > 0) {
      return orders[0];
    }

    console.warn(`[fetchOrderById] No order found in results array for orderId:`, orderId);
    return null;
  },
  setEmptyCurrentOrder({ commit }, isEmptyCurrentOrder) {
    commit('setEmptyCurrentOrder',isEmptyCurrentOrder)
  },
  updateLocalIds({ commit }) {
    commit('updateLocalIds')
  },
  setSyncedOrderStatus: ({ commit }, syncedOrderStatus) => commit('setSyncedOrderStatus', syncedOrderStatus),
  setSyncedOrder: ({ commit }, syncedOrder) => commit('setSyncedOrder', syncedOrder),
  setActiveOrderState: ({commit},isActiveOrder) => commit('setActiveOrderState',isActiveOrder),
  setRefundedOrderTotalDiscount: ({ commit }, refundedOrderDiscountTotalDetails) => commit('setRefundedOrderTotalDiscount', refundedOrderDiscountTotalDetails),
  fetchAllRefundedItems: async ({ commit }) => {
    const allRefundedItems = (await DashboardService.getAllRefundedOrders()).results
    commit('fetchRefundedOrderList', allRefundedItems)
  },
  fetchOnlineOrders: async ({ commit, state, dispatch }, orderStatus) => {
    if (window.navigator.onLine) {
      try {
        const orders = (await DashboardService.getOnlineOrdersList(orderStatus)).results
        // Collect pending online orders that haven't been auto-approved yet.
        // Check local state BEFORE the commit so we see the pre-merge receipt_kitchen_printed
        // value (local state is ahead of the server while the outbox is still draining).
        const ordersForAutoSend = (orders || []).filter(o => {
          if (!o || o.type !== 1 || o.status !== ORDER_STATUS.PENDING) return false;
          // Do NOT use server's receipt_kitchen_printed — the backend may set it for
          // payment-confirmation emails before the POS has approved the order.
          // Check local state only (autoSendOnlineKitchen also checks local state).
          const localOrder = state.orders.find(lo =>
            (o.id && lo.id === o.id) || (o.local_id && lo.local_id === o.local_id)
          );
          return !(localOrder && localOrder.receipt_kitchen_printed);
        });
        await commit("updateOrders", orders);
        for (const order of ordersForAutoSend) {
          await dispatch('autoSendOnlineKitchen', order);
        }
      } catch (error) {
        console.error('Failed to fetch online orders:', error.message);
        // Don't throw - allow app to continue with cached data
      }
    }
  },
  fetchOpenOrders: async ({ commit, state, dispatch }) => {
    if (window.navigator.onLine) {
      try {
        const retrievedOrders = (await DashboardService.getOpenOrders()).results
        const newOrderArray = []
        retrievedOrders.forEach(retrievedOrder => {
          const newOrder = state.orders.find(order => (order.id === retrievedOrder.id) && ((order.status > retrievedOrder.status) ) )
          if (!newOrder) {
            retrievedOrder.synced_at = (new Date).toISOString();
            newOrderArray.push(retrievedOrder)
          }
        })
      await commit("updateOrders", newOrderArray);
      commit('fetchOrderListByStatus', newOrderArray);
      // Ghost removal only acts on orders with a server id. An order whose
      // blank-create was interrupted stays id-less (createRemoteOrder makes a
      // single attempt) and would otherwise be un-removable forever, so ask the
      // server for ids of local-only orders first. Orders the server has never
      // seen get no mapping and are kept.
      const localOnlyIds = state.orders.filter(o => o.id == null && o.local_id).map(o => o.local_id);
      if (localOnlyIds.length > 0) {
        await dispatch('resolveOrderIdsByLocalIds', { local_ids: localOnlyIds });
      }
      // Remove "ghosts": orders with server id that are no longer in the server's open list.
      // The backend sits behind a queue and can trail Ably by minutes, so the
      // server list alone is not safe to prune against: an order Ably still
      // shows as open (e.g. just created on another terminal) is kept even if
      // the server hasn't caught up yet.
      const keepIds = new Set(retrievedOrders.map(o => o.id).filter(id => id != null).map(String));
      if (liveObjectsService.isReady()) {
        const ablyOrders = await liveObjectsService.getAllOrdersSnapshot();
        ablyOrders.forEach(o => {
          if (o && o.id != null && !isTerminalOrderStatus(o.status)) keepIds.add(String(o.id));
        });
      }
      commit('removeOrdersNotInOpenSet', Array.from(keepIds));
    } catch (error) {
      console.error('Failed to fetch open orders:', error.message);
        // Don't throw - allow app to continue with cached data
      }
    }
  },
  fetchUpdatedTableOrders: async () => {
    if (window.navigator.onLine) {
      try {
        // const recentOrder = (await DashboardService.getRecentOrders())
        // if (orderId) {
        //   let updatedOrders = recentOrder.find(order => order.id === orderId && order.status !== ORDER_STATUS.OPEN)
        //   // Only process if order was found in recent orders
        //   if (!updatedOrders) {
        //     return;
        //   }
        //   const index = state.orders.findIndex(order => order.id === orderId)
        //   const secondIndex = state.orderList.findIndex(order => order.id === orderId)
        //   if (index > -1) {
        //     // Ensure local_id is set (use existing or set to id)
        //     const existingOrder = state.orders[index];
        //     updatedOrders = {
        //       ...updatedOrders,
        //       "synced_at": (new Date).toISOString(),
        //       "local_id": updatedOrders.local_id || existingOrder.local_id || updatedOrders.id
        //     }
        //     await commit("updateOrders", updatedOrders);
        //   }
        //   if (secondIndex > -1) {
        //     state.orderList[secondIndex] = { ...updatedOrders }
        //   }
        // }
        // else {
        //   // await commit("updateOrders", recentOrder);
        //   recentOrder.forEach(async updatedOrder => {
        //     if (updatedOrder && updatedOrder.status !== ORDER_STATUS.OPEN) {
        //       const index = state.orders.findIndex(order => order.id === updatedOrder.id)
        //       const secondIndex = state.orderList.findIndex(order => order.local_id === updatedOrder.local_id || order.id === updatedOrder.id)
        //       if (index > -1) {
        //         // Ensure local_id is set (use existing or set to id)
        //         const existingOrder = state.orders[index];
        //         updatedOrder = {
        //           ...updatedOrder,
        //           "synced_at": (new Date).toISOString(),
        //           "local_id": updatedOrder.local_id || existingOrder.local_id || updatedOrder.id
        //         }
        //         await commit("updateOrders", updatedOrder);
        //       }
        //       else if (secondIndex > -1) {
        //         state.orderList[secondIndex] = { ...updatedOrder }
        //       }
        //     }
        //   })
        // }

        // indexDbService.saveToDb("orders",recentOrder)

        // commit("setTableOrders", recentOrder)
      } catch (error) {
        console.error('Failed to fetch updated table orders:', error.message);
        // Don't throw - allow app to continue with cached data
      }
    }
  },
  SetOfflineOrdersToTable: ({ commit, state }) => {
    commit("setTableOrders", state.orders)
  },
  // ── Phase 5 (index wiring): intentional cross-status, single-scan load ──────
  // This is the ONLY IndexedDB order-load path, and it deliberately stays on the
  // full-store `getDbData('orders')` cursor rather than the `getOrdersByStatus` /
  // `getOrdersUpdatedSince` index helpers. Reasons (all behavior-preserving):
  //   1. Cross-status hydration. The single scan feeds three slices that the rest
  //      of the app reads across EVERY status: `state.orders` (getAllOrders,
  //      getOrderByIdOrLocalId, getAllActiveOrders, store getter getTableWithOrders'
  //      OPEN filter, the active-order proxy, promotions, cross-terminal merge, and
  //      OrdersList's realtime/offline tabs for completed/cancelled/refunded),
  //      `state.orderList` (OrdersList's offline list display, all statuses), and
  //      `state.refundedOrdersList` (the refunded subset). Narrowing any of these to
  //      one status would drop orders other consumers need (e.g. reprinting an old
  //      completed receipt offline via getOrderByIdOrLocalId), so per the refactor
  //      guardrails it is left loading-all.
  //   2. The index helpers are SUBSET primitives, not faster full loads. A single
  //      `getDbData` cursor is already the minimum number of scans for a full
  //      hydration; reassembling the full set from five `getOrdersByStatus` ranges
  //      would ADD scans, and `getOrdersUpdatedSince`/`getOrdersByStatus` skip any
  //      record missing the indexed keyPath (no `updated_at`, null `status`), so
  //      neither returns the same set as `getDbData` for an unbounded load.
  // The refunded slice below is derived from the already-loaded full set (zero
  // extra scans), so it does not benefit from the status index either. The index
  // helpers remain wired in dbHelper for future subset-only consumers (e.g. a
  // dedicated status-scoped or "updated since last sync" reader that does not feed
  // the shared cross-status `state.orders`).
  async fetchOrdersFromDb({ commit }) {
    const orders = await indexDbService.getDbData('orders')
    const localOrders = orders.map(order => ({ ...order, __fromIndexedDb: true }));
    // const refundedOrders =  await indexDbService.getDbData('refundedOrderList')
    await commit("fetchOrderListByStatus", orders);
    await commit("updateOrders", localOrders);
    await commit('fetchRefundedOrderList', orders.filter(order=>order.status === ORDER_STATUS.REFUNDED))
  },
  /**
   * Resolve server IDs for orders by local_ids (check-ids API).
   * Used by PRE-CHECK (on coming online) and POST-SEND (after sending to queue).
   * Skips API call when all given orders already have id (avoids redundant check-ids).
   */
  async resolveOrderIdsByLocalIds({ commit, state }, { local_ids: localIds }) {
    if (!localIds || localIds.length === 0) return;
    if (!window.navigator.onLine) return;
    if (Date.now() < resolveOrderIdsBackoffUntil) return;

    const stillMissingId = localIds.filter(localId => {
      const order = state.orders.find(o => o.local_id === localId);
      return order && order.id == null;
    });
    if (stillMissingId.length === 0) return;

    try {
      const response = await DashboardService.checkOrderIds({ local_ids: stillMissingId });
      const mappings = response.mappings || (response.data && response.data.mappings);
      if (!mappings || !Array.isArray(mappings)) return;

      resolveOrderIdsBackoffUntil = 0;
      const nowIso = (new Date).toISOString();
      for (const mapping of mappings) {
        const orderIndex = state.orders.findIndex(o => o.local_id === mapping.local_id);
        if (orderIndex === -1) continue;

        // updateOrderId already stamps synced_at and persists to IndexedDB when
        // synced_at is provided, so no direct state mutation is needed here.
        commit("updateOrderId", {
          local_id: mapping.local_id,
          id: mapping.id,
          number: mapping.id != null ? mapping.id.toString() : undefined,
          synced_at: nowIso
        });
        broadcastAssignedOrderId(state, mapping.local_id);
      }
    } catch (e) {
      console.warn("[Sync] resolveOrderIdsByLocalIds failed:", e);
      resolveOrderIdsBackoffUntil = Date.now() + REMOTE_ORDER_SYNC_BACKOFF_MS;
    }
  },

  async enqueueOrderSync({ commit }, { order, trigger = "unknown" } = {}) {
    const payloadSource = order || null;
    const entry = buildOrderSyncOutboxEntry(payloadSource, trigger);
    if (!entry) return null;

    const existingEntries = await indexDbService.getDbData('orderSyncOutbox');
    const existingEntry = existingEntries.find(item => item.key === entry.key);
    const revisionChanged = !existingEntry || existingEntry.revision !== entry.revision;
    const queuedEntry = {
      ...entry,
      attempts: revisionChanged ? 0 : existingEntry.attempts || 0,
      created_at: existingEntry ? existingEntry.created_at : entry.created_at,
      last_error: revisionChanged ? null : existingEntry.last_error || null,
      next_retry_at: revisionChanged ? null : existingEntry.next_retry_at || null
    };

    await indexDbService.saveToDb('orderSyncOutbox', queuedEntry);

    commit('markOrderUpdatedFlag', {
      order_id: entry.order_id,
      order_local_id: entry.order_local_id
    });

    return queuedEntry;
  },

  async drainOrderSyncOutbox({ state, commit }) {
    if (!window.navigator.onLine) {
      return [];
    }
    if (drainOrderSyncOutboxPromise) {
      drainOrderSyncOutboxNeedsRerun = true;
      return drainOrderSyncOutboxPromise;
    }

    drainOrderSyncOutboxPromise = (async () => {
      const sentLocalIds = [];
      let shouldContinueDraining = true;

      while (shouldContinueDraining) {
        if (reconcileOrdersInFlight) {
          await reconcileOrdersInFlight.catch(() => {});
        }
        const now = Date.now();
        const entries = await indexDbService.getDbData('orderSyncOutbox');
        const dueEntries = entries
          .filter(entry => !entry.next_retry_at || new Date(entry.next_retry_at).getTime() <= now)
          .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));

        if (dueEntries.length === 0) {
          if (drainOrderSyncOutboxNeedsRerun) {
            drainOrderSyncOutboxNeedsRerun = false;
            continue;
          }
          shouldContinueDraining = false;
          continue;
        }

        for (const entry of dueEntries) {
          let latestOrder = state.orders.find(order => {
            const key = getOrderSyncKey(order);
            return key && key === entry.key;
          });
          let latestEntry = latestOrder ? buildOrderSyncOutboxEntry(latestOrder, entry.trigger || "outbox") : null;
          let queuedPayload = normalizeOrderForSync(entry.payload);

          // A server-known order that is gone from local state was removed as a
          // ghost (the server no longer lists it as open). Uploading the queued
          // open copy would resurrect it — only closed intents may still go out.
          if (!latestOrder && queuedPayload && queuedPayload.id && !isTerminalOrderStatus(queuedPayload.status)) {
            await indexDbService.deleteFromDb('orderSyncOutbox', entry.key);
            continue;
          }

          let selectedCandidate = selectOrderSyncCandidate(queuedPayload, latestEntry);
          let payload = selectedCandidate.payload;
          let sentRevision = selectedCandidate.revision;
          let entryForRetry = entry;

          if (!isQueueableOrder(payload)) {
            await indexDbService.deleteFromDb('orderSyncOutbox', entry.key);
            continue;
          }

          try {
            await DashboardService.syncOrder(payload);

            const currentOrder = state.orders.find(order => {
              const key = getOrderSyncKey(order);
              return key && key === entry.key;
            });
            const currentEntry = currentOrder ? buildOrderSyncOutboxEntry(currentOrder, entry.trigger || "outbox") : null;
            const currentRevision = currentEntry ? currentEntry.revision : sentRevision;

            // If the order changed while this request was in-flight (e.g. OPEN sent,
            // then cashier completed the order), never mark the newer local order as
            // synced and never delete the newer outbox row. Queue the latest revision
            // and let the loop send it next.
            if (currentRevision !== sentRevision) {
              if (currentEntry) {
                await indexDbService.saveToDb('orderSyncOutbox', currentEntry);
              }
              continue;
            }

            const currentOutbox = (await indexDbService.getDbData('orderSyncOutbox')).find(item => item.key === entry.key);
            if (currentOutbox &&
                currentOutbox.revision &&
                currentOutbox.revision !== entry.revision &&
                currentOutbox.revision !== sentRevision) {
              continue;
            }

            const syncedAt = new Date().toISOString();
            commit("markOrderSynced", {
              id: payload.id,
              local_id: payload.local_id,
              synced_at: syncedAt,
              last_synced_revision: sentRevision
            });
            await indexDbService.deleteFromDb('orderSyncOutbox', entry.key);
            if (payload.local_id) {
              sentLocalIds.push(payload.local_id);
            }
          } catch (err) {
            const currentOutbox = (await indexDbService.getDbData('orderSyncOutbox')).find(item => item.key === entry.key);
            if (currentOutbox && currentOutbox.revision && currentOutbox.revision !== entry.revision) {
              continue;
            }
            const attempts = (entry.attempts || 0) + 1;
            const retryDelayMs = Math.min(60000, 1000 * Math.pow(2, Math.min(attempts, 6)));
            await indexDbService.saveToDb('orderSyncOutbox', {
              ...entryForRetry,
              payload,
              revision: sentRevision,
              attempts,
              last_error: err && err.message ? err.message : String(err),
              updated_at: new Date().toISOString(),
              next_retry_at: new Date(Date.now() + retryDelayMs).toISOString()
            });
          }
        }

        if (drainOrderSyncOutboxNeedsRerun) {
          drainOrderSyncOutboxNeedsRerun = false;
        }
      }

      return sentLocalIds;
    })();

    try {
      return await drainOrderSyncOutboxPromise;
    } finally {
      drainOrderSyncOutboxPromise = null;
    }
  },

  async uploadOfflineOrders({ state, dispatch }) {
    if (!window.navigator.onLine) {
      return;
    }

    if (state.orders.length === 0) {
      await dispatch('fetchOrdersFromDb');
    }

    // Queue only remaining unsynced orders. The durable outbox coalesces
    // by order key so repeated triggers update the latest payload instead of
    // posting duplicate requests.
    const offlineOrders = state.orders.filter(order => {
      if (!isQueueableOrder(order)) return false;
      const currentRevision = getOrderSyncStateSignature(order);
      return currentRevision !== (order.last_synced_revision || "");
    });
    if (offlineOrders.length === 0) {
      await dispatch('drainOrderSyncOutbox');
      const pendingOutbox = await indexDbService.getDbData('orderSyncOutbox');
      if (pendingOutbox.length > 0) {
        throw new Error("Order sync outbox still has pending orders");
      }
      return;
    }

    for (const order of offlineOrders) {
      await dispatch('enqueueOrderSync', { order, trigger: 'offline_upload' });
    }
    await dispatch('drainOrderSyncOutbox');
    const pendingOutbox = await indexDbService.getDbData('orderSyncOutbox');
    const pendingKeys = new Set(offlineOrders.map(order => getOrderSyncKey(order)).filter(Boolean));
    const hasPendingQueuedOrders = pendingOutbox.some(entry => pendingKeys.has(entry.key));
    if (hasPendingQueuedOrders) {
      throw new Error("Order sync outbox still has pending orders");
    }

  },
  updateOrder({ commit }, orders) {
    commit('updateOrders',orders)
  },
  setOrderInitailSync({commit},initialSync){
    commit("setOrderInitailSync",initialSync)
  },
  setWholeOrderDiscount: ({commit},discount) => commit('setWholeOrderDiscount',discount),
  setSelectedOrder: ({commit}, orderId) => {commit('setSelectedOrder',orderId)},
  applyRealtimeOrder: async ({ state, commit, rootGetters, dispatch }, orderPayload) => {
    if (!orderPayload) return;

    // Check if this is an update to an existing order with new items
    const existingOrderIndex = state.orders.findIndex(order => {
      if (orderPayload.id && order.id === orderPayload.id) return true;
      if (orderPayload.local_id && order.local_id === orderPayload.local_id) return true;
      return false;
    });

    let shouldNotify = false;
    let notificationMessage = '';
    const isOnlineOrder = orderPayload.type === 1;
    // A terminal that missed the original PENDING broadcast (e.g. was offline)
    // will see the first update it DOES receive (approve/hold/reject/complete)
    // as "not in state.orders yet" too. Status must be PENDING for this to be
    // an actual new order, otherwise a reject/hold looks identical to a new one.
    // Table orders never pass through PENDING (they're created directly as OPEN),
    // so there's no earlier broadcast to have missed - any first sighting of an
    // order with a table assigned is genuinely new.
    const isGenuinelyNewOrder = existingOrderIndex === -1 && isOnlineOrder &&
      (orderPayload.status === ORDER_STATUS.PENDING || !!orderPayload.table);

    // NEW ORDER: Notify for new online orders
    if (isGenuinelyNewOrder) {
      shouldNotify = true;
      const orderIdentifier = orderPayload.id || orderPayload.local_id || orderPayload.number;

      // Get table name if order has a table
      let locationInfo = `Order #${orderIdentifier}`;
      if (orderPayload.table) {
        const table = rootGetters['config/getTableById'](orderPayload.table);
        if (table && table.table_name) {
          locationInfo = `${table.table_name}`;
        }
      }

      const itemCount = orderPayload.items?.length || 0;
      notificationMessage = `New order received: ${locationInfo} (${itemCount} ${itemCount === 1 ? 'item' : 'items'})`;
    }

    // Check if order exists and has new items
    if (existingOrderIndex !== -1) {
      const existingOrder = state.orders[existingOrderIndex];
      const newItems = orderPayload.items || [];
      const existingItems = existingOrder.items || [];

      // Check if the number of items increased
      if (newItems.length > existingItems.length) {
        const itemsDiff = newItems.length - existingItems.length;

        // Identify specifically which items are new to check their terminal_id
        const existingIds = new Set(existingItems.map(i => i.id || i.local_id));
        const addedItems = newItems.filter(i => !existingIds.has(i.id || i.local_id));

        // Check if any of the added items are from online source (terminal_id < 1 or null/undefined)
        // If items are added by a terminal (terminal_id >= 1), we don't want to notify
        const hasOnlineItems = addedItems.some(item => !item.terminal_id || parseInt(item.terminal_id) < 1);

        if (hasOnlineItems) {
          shouldNotify = true;
          const orderIdentifier = orderPayload.id || orderPayload.local_id;

          // Get table name if order has a table
          let locationInfo = `Order #${orderIdentifier}`;
          if (orderPayload.table) {
            const table = rootGetters['config/getTableById'](orderPayload.table);
            if (table && table.table_name) {
              locationInfo = `${table.table_name} / Order #${orderIdentifier}`;
            }
          }

          notificationMessage = `${itemsDiff} new ${itemsDiff === 1 ? 'item' : 'items'} added to ${locationInfo}`;
        }
      } else {
        // Check if any existing items have quantity changes
        const existingItemsMap = new Map(existingItems.map(item => [item.id || item.local_id, item]));
        for (const newItem of newItems) {
          const itemKey = newItem.id || newItem.local_id;
          const existingItem = existingItemsMap.get(itemKey);

          if (existingItem && newItem.quantity > existingItem.quantity) {
            shouldNotify = true;
            const orderIdentifier = orderPayload.id || orderPayload.local_id;

            // Get table name if order has a table
            let locationInfo = `Order #${orderIdentifier}`;
            if (orderPayload.table) {
              const table = rootGetters['config/getTableById'](orderPayload.table);
              if (table && table.table_name) {
                locationInfo = `${table.table_name} / Order #${orderIdentifier}`;
              }
            }

            notificationMessage = `Items updated in ${locationInfo}`;
            break;
          }
        }
      }
    }

    // Apply the order update
    await commit('updateOrders', orderPayload);

    // For NEW online orders arriving via Ably, trigger auto-send immediately.
    // autoSendOnlineKitchen loads products from IndexedDB if they aren't in Vuex
    // yet, so the "products not loaded" concern is already handled there.
    // Double-send is prevented by receipt_kitchen_printed and _autoSendInFlight guards.
    if (isGenuinelyNewOrder) {
      dispatch('autoSendOnlineKitchen', orderPayload).catch(() => {});
    }

    // Add notification only for Online Orders (type === 1)
    if (shouldNotify && notificationMessage && isOnlineOrder) {
      try {
        dispatch('notifications/addNotification', {
          title: isGenuinelyNewOrder ? "New Online Order" : "Order Updated",
          message: notificationMessage,
          data: {
            call: isGenuinelyNewOrder ? "newOnlineOrder" : "orderUpdate",
            instance_id: orderPayload.id || orderPayload.local_id,
            table: orderPayload.table
          }
        }, { root: true });
      } catch (error) {
        console.error("Error adding order update notification:", error);
      }
    }
  },
  autoSendOnlineKitchen: async ({ dispatch, rootGetters, state }, order) => {
    const flightKey = order.id != null ? String(order.id)
      : (order.local_id != null ? String(order.local_id) : null);
    if (flightKey && _autoSendInFlight.has(flightKey)) return;
    if (flightKey) _autoSendInFlight.add(flightKey);
    try {
      // Use local state as the "already processed" guard — the server may set
      // receipt_kitchen_printed for email receipts before the POS approves the order.
      const localOrder = state.orders.find(lo =>
        (order.id && lo.id === order.id) || (order.local_id && lo.local_id === order.local_id)
      );
      if (localOrder && localOrder.receipt_kitchen_printed) return;

      const terminal = rootGetters['config/terminal'];
      if (!isOnlineKitchenAutoSendEnabled(terminal)) return;
      let products = rootGetters['config/products'] || [];
      // Auto-send runs app-wide (Pusher), often before a view loads the catalog
      // into Vuex. Fall back to IndexedDB so printer assignments resolve.
      if (!products.length) {
        await dispatch('config/fetchProductsFromDb', null, { root: true });
        products = rootGetters['config/products'] || [];
      }
      const employee = rootGetters.employee;
      const result = await autoSendOnlineOrderToKitchen({ order, products, terminal, employee });
      if (result.sent) {
        dispatch('saveOrder', result.order);
        dispatch('enqueueOrderSync', { order: result.order, trigger: 'online_kitchen_auto_send' })
          .then(() => dispatch('drainOrderSyncOutbox'))
          .catch(() => {});
      }
    } catch (error) {
      console.warn('autoSendOnlineKitchen failed', error);
    } finally {
      if (flightKey) _autoSendInFlight.delete(flightKey);
    }
  },
  // Triggered by the Pusher `fetchPendingOrder` event when a new online order
  // arrives. Online orders are delivered over Pusher (not Ably), so this fetches
  // the incoming order and fires it to the kitchen when the
  // `autosend_kitchen_online` setting is enabled. Works app-wide, not just on
  // the Online Orders page.
  autoSendOnlineKitchenById: async ({ commit, dispatch, rootGetters }, orderId) => {
    try {
      if (!orderId || orderId === 'undefined') return;

      // Avoid re-firing a concurrent fetch for the same order (Ably, fetchOnlineOrders,
      // or another Pusher timer racing this one). Do NOT also bail on
      // receipt_kitchen_printed here — a table order gets re-fired on every added
      // round (more items, same order id), and that flag stays true from the FIRST
      // round forever, which would silently stop later rounds from ever landing in
      // POS state. Per-item sent_to_kitchen tracking downstream already prevents
      // re-printing items that went out in an earlier round.
      if (_autoSendInFlight.has(String(orderId))) return;

      // A freshly-arrived online order has no `related_terminal` yet, so the
      // terminal-scoped `/orderlist?id=` endpoint won't return it. Fetch from the
      // online pending list (the same source the Online Orders page uses) so this
      // works regardless of which page the user is on.
      // Pre-paid orders (e.g. Stripe buy orders) have their status set to OPEN
      // synchronously during checkout before Pusher fires, so they never appear
      // in the PENDING list. Fall back to OPEN if not found in PENDING.
      //
      // This fetch always runs, even when autosend-to-kitchen is disabled: it's
      // the only path that lands the order (and its table assignment) in POS
      // state when Ably is the active realtime channel, since the Tables view
      // has no independent polling fallback (unlike Online Orders, which polls
      // every 3 min regardless). The autosend setting only gates the actual
      // kitchen send below, not whether the order becomes visible on the POS.
      const pendingOnline = (await DashboardService.getOnlineOrdersList(ORDER_STATUS.PENDING)).results || [];
      let order = pendingOnline.find(o => o && String(o.id) === String(orderId));
      if (!order) {
        const openOnline = (await DashboardService.getOnlineOrdersList(ORDER_STATUS.OPEN)).results || [];
        order = openOnline.find(o => o && String(o.id) === String(orderId));
      }
      if (!order || order.type !== 1) return;
      // Do NOT check order.receipt_kitchen_printed from the server here — the backend
      // may set it for payment-confirmation emails before the POS approves the order.
      // Terminal status is the reliable "already done" signal.
      const isTerminal = order.status === ORDER_STATUS.COMPLETED ||
        order.status === ORDER_STATUS.CANCELLED ||
        order.status === ORDER_STATUS.REFUNDED;
      if (isTerminal) return;

      // Land the order in POS state so the UI reflects the arrival too.
      await commit('updateOrders', order);

      const terminal = rootGetters['config/terminal'];
      if (isOnlineKitchenAutoSendEnabled(terminal)) {
        await dispatch('autoSendOnlineKitchen', order);
      }
    } catch (error) {
      console.warn('autoSendOnlineKitchenById failed', error);
    }
  },
  // Triggered by the Pusher `fetchPendingKitchenHubOrder` event. The Django
  // webhook receiver does the minimum possible (verify + resolve brand) and
  // relays here; this action owns building the actual Biyo order: fetch the
  // order detail from KitchenHub, map it to a local order object, save it
  // (which broadcasts over Ably to other terminals), and hand it to the same
  // order-queue sync outbox every other order type uses. local_id follows the
  // standard terminal/timestamp convention (not KitchenHub-specific), so
  // idempotency across concurrent terminals is checked by `kitchenhub_order_id`
  // instead — carried as its own field on the order object and passed through
  // to OrderCustomSyncView for the same purpose server-side.
  receiveKitchenHubOrder: async ({ dispatch, state, rootGetters }, { kitchenhub_order_id, brand_id } = {}) => {
    if (!kitchenhub_order_id || !brand_id) return;
    const flightKey = String(kitchenhub_order_id);
    if (_kitchenHubReceiveInFlight.has(flightKey)) return;

    if (state.orders.some(o => o.kitchenhub_order_id === String(kitchenhub_order_id))) return;

    _kitchenHubReceiveInFlight.add(flightKey);
    try {
      const detail = await DashboardService.getKitchenHubOrder(kitchenhub_order_id, brand_id);

      let products = rootGetters['config/products'] || [];
      if (!products.length) {
        await dispatch('config/fetchProductsFromDb', null, { root: true });
        products = rootGetters['config/products'] || [];
      }

      const terminal = rootGetters['config/terminal'];
      const order = buildOrderFromKitchenHub(detail, products, terminal ? terminal.id : null);
      if (state.orders.some(o => o.kitchenhub_order_id === order.kitchenhub_order_id)) return;

      dispatch('saveOrder', order);
      await dispatch('enqueueOrderSync', { order, trigger: 'kitchenhub_order' });
      await dispatch('drainOrderSyncOutbox');

      const providerName = order.provider_name || 'KitchenHub';
      dispatch('notifications/addNotification', {
        title: `New ${providerName} Order`,
        message: `A new ${providerName} order has been received. Click to view.`,
        data: { call: 'newOnlineOrder', instance_id: order.local_id }
      }, { root: true });
    } catch (error) {
      console.warn('receiveKitchenHubOrder failed', error);
    } finally {
      _kitchenHubReceiveInFlight.delete(flightKey);
    }
  },
  applyRealtimeDeletion: ({ commit }, payload) => {
    commit('removeOrderByIdentifier', payload || {});
  },
  broadcastOrderRemoval: (context, identifier) => {
    broadcastOrderDeletion(identifier || {});
  }

};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
